import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getShiftReport } from '../../../services/timesheetService';
import formatMinutes from '../../../utils/formatMinutes';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { useTaFilterColumns } from './taFilterColumns';
import { useTaListFetch } from './useTaListFetch';

const countColumn = (name, label) => ({
  name, label, options: { filter: false, sort: false, customBodyRender: (v) => v ?? 0 },
});

/**
 * AKM POC §7.4 — Vardiya Raporu: dönemde yayınlanmış/tamamlanmış vardiyası olan kişi başına vardiya sayısı, planlanan süre,
 * gece / hafta sonu / resmî tatil / nöbet / fazla mesai vardiyaları ve takaslar. Dönem görünmez tarih sütunu (boşsa bu ay,
 * en çok 93 gün — fazlası sunucuda taReport.rangeTooLong).
 */
export function useShiftReport() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const list = useTaListFetch(getShiftReport);
  const filterColumns = useTaFilterColumns(t);

  const columns = useMemo(() => {
    const period = dateFilterColumn('date', t('taReports.filters.period'), lang);
    return [
      { name: 'userId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'fullName', label: t('timesheet.employee'),
        options: {
          filter: true, sort: false, filterType: 'textField',
          filterOptions: { filterKey: 'userFullName[like]' },
        },
      },
      {
        name: 'registrationNumber', label: t('taReports.attendanceCard.registrationNumber'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'departmentName', label: t('taReports.attendanceCard.department'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      countColumn('shiftCount', t('taReports.shiftReport.shiftCount')),
      {
        name: 'plannedMinutes', label: t('taReports.shiftReport.plannedMinutes'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      countColumn('nightShiftCount', t('taReports.shiftReport.nightShiftCount')),
      countColumn('weekendShiftCount', t('taReports.shiftReport.weekendShiftCount')),
      countColumn('holidayShiftCount', t('taReports.shiftReport.holidayShiftCount')),
      countColumn('dutyShiftCount', t('taReports.shiftReport.dutyShiftCount')),
      countColumn('plannedOvertimeShiftCount', t('taReports.shiftReport.plannedOvertimeShiftCount')),
      countColumn('swapCount', t('taReports.shiftReport.swapCount')),
      { ...period, options: { ...period.options, display: 'excluded', viewColumns: false, download: false, sort: false } },
      ...filterColumns,
    ];
  }, [t, lang, filterColumns]);

  return { ...list, columns };
}

export default useShiftReport;
